/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Notification Provider - Context for global notifications
 * Updated: 2026-01-09
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { notificationManager, Notification } from '../../services/NotificationManager';
import LoadingBanner from './LoadingBanner';
import ErrorAlert from './ErrorAlert';
import SuccessNotification from './SuccessNotification';

interface NotificationContextType {
  showLoading: (message: string, onCancel?: () => void) => string;
  showError: (message: string, onRetry?: () => void | Promise<void>, details?: string) => string;
  showSuccess: (message: string, duration?: number) => string;
  showInfo: (message: string, duration?: number) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    const unsubscribe = notificationManager.subscribe(setNotifications);
    return unsubscribe;
  }, []);

  const value: NotificationContextType = {
    showLoading: notificationManager.showLoading.bind(notificationManager),
    showError: notificationManager.showError.bind(notificationManager),
    showSuccess: notificationManager.showSuccess.bind(notificationManager),
    showInfo: notificationManager.showInfo.bind(notificationManager),
    dismiss: notificationManager.dismiss.bind(notificationManager),
    dismissAll: notificationManager.dismissAll.bind(notificationManager),
  };

  // Separate loading, error, and success notifications
  const loadingNotification = notifications.find(n => n.type === 'loading');
  const errorNotifications = notifications.filter(n => n.type === 'error');
  const successNotifications = notifications.filter(n => n.type === 'success');

  return (
    <NotificationContext.Provider value={value}>
      {children}

      {/* Loading Banner - Only one at a time */}
      {loadingNotification && (
        <LoadingBanner
          message={loadingNotification.message}
          onCancel={loadingNotification.onAction}
          showProgressBar={false}
        />
      )}

      {/* Error Alerts - Stack below loading banner */}
      <div className="fixed top-0 left-0 right-0 pt-16 z-30 pointer-events-none">
        <div className="flex flex-col gap-3 p-4 pointer-events-auto max-w-md mx-auto md:mx-0 md:max-w-lg">
          {errorNotifications.map((notification) => (
            <ErrorAlert
              key={notification.id}
              message={notification.message}
              details={notification.details}
              onDismiss={() => notificationManager.dismiss(notification.id)}
              onRetry={
                notification.onAction
                  ? async () => {
                      await notification.onAction?.();
                      notificationManager.dismiss(notification.id);
                    }
                  : undefined
              }
            />
          ))}
        </div>
      </div>

      {/* Success Notifications - Stack below header */}
      <div className="fixed top-16 left-0 right-0 z-35 pointer-events-none">
        <div className="flex flex-col gap-2 p-4 pointer-events-auto max-w-md mx-auto">
          {successNotifications.map((notification) => (
            <SuccessNotification
              key={notification.id}
              message={notification.message}
              onDismiss={() => notificationManager.dismiss(notification.id)}
            />
          ))}
        </div>
      </div>
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
